import { isVerificationMethodEnabled, validateVerificationFilters } from '../../utils/verificationFilterUtils';
import { isIdentificationMethodEnabled, validateIdentificationFilters } from '../../utils/identificationFilterUtils';

/**
 * Columns of the SBOM table minus the ones disabled by feature flags.
 * Shared by the flat SBOM table and the nested table of grouped SBOM rows.
 */
export const getSbomFlagFilteredColumns = ({ columns = [], isIdentifiedViaEnabled, isConfidenceEnabled, isAiRelatedDependenciesEnabled }) =>
  columns.filter(column => {
    // Exclude verification column when identifiedVia is enabled
    if (column.name === 'verification' && isIdentifiedViaEnabled) return false;
    // Exclude identifiedVia column when flag is disabled
    if (column.name === 'identifiedVia' && !isIdentifiedViaEnabled) return false;
    // Exclude confidence column when flag is disabled
    if (column.name === 'confidence' && !isConfidenceEnabled) return false;
    // Exclude AI Architecture column when AI-related dependencies flag is disabled
    if (column.name === 'aiArchitecture' && !isAiRelatedDependenciesEnabled) return false;
    return true;
  });

const withEnabledFilterValues = (column, isEnabled, enabledMethods) =>
  enabledMethods && column.filterValues
    ? { ...column, filterValues: column.filterValues.filter(filterValue => isEnabled(filterValue.value, enabledMethods)) }
    : column;

/**
 * Removes the verification / identifiedVia filter options that the organization has disabled.
 * Columns are returned untouched until the enabled methods are known. Does not mutate the source columns.
 */
export const applyEnabledMethodsToColumns = (columns = [], { enabledVerificationMethods, enabledIdentificationMethods } = {}) =>
  columns.map(column => {
    if (column.name === 'verification') return withEnabledFilterValues(column, isVerificationMethodEnabled, enabledVerificationMethods);
    if (column.name === 'identifiedVia') return withEnabledFilterValues(column, isIdentificationMethodEnabled, enabledIdentificationMethods);
    return column;
  });

/** SBOM columns after applying the feature flags and the organization's enabled verification / identification methods. */
export const getSbomColumns = ({
  columns,
  isIdentifiedViaEnabled,
  isConfidenceEnabled,
  isAiRelatedDependenciesEnabled,
  enabledVerificationMethods,
  enabledIdentificationMethods
}) =>
  applyEnabledMethodsToColumns(getSbomFlagFilteredColumns({ columns, isIdentifiedViaEnabled, isConfidenceEnabled, isAiRelatedDependenciesEnabled }), {
    enabledVerificationMethods,
    enabledIdentificationMethods
  });

/** Drops (or trims) verification / identifiedVia filters that use methods the organization has disabled. */
export const validateSbomMethodFilters = (filters, { enabledVerificationMethods, enabledIdentificationMethods } = {}) =>
  validateIdentificationFilters(validateVerificationFilters(filters, enabledVerificationMethods), enabledIdentificationMethods);
